import React, { useState } from "react";
import Modal from "./Modal.tsx";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import InputField from "../Forms/InputField.js";
import SelectField from "../Forms/SelectField.js";
import { createServiceUser } from "../../services/users.js";
import toast from "react-hot-toast";
import Checkbox from "../Forms/Checkbox.js";
import { Controller } from "react-hook-form";
import { Link } from "react-router-dom";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { dateFormat } from "../../utils/functions.js";
// Validation schema using Yup
const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  gender: Yup.string().required("Gender is required"),
  dateOfBirth: Yup.date().required("Date of birth is required"),
  ethnicity: Yup.string().required("Ethnicity is required"),
  startDate: Yup.date().required("Start date is required"),
  address: Yup.string().required("Address is required"),
  postalCode: Yup.string().required("Postcode is required"),
  telephoneNumber: Yup.string().required("Phone number is required"),
});

const CreateServiceUser = ({ isModalOpen, setIsModalOpen, reloadUsers }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [newUseId, setNewUserId] = useState(null);

  // Initialize React Hook Form with validation schema
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // Handle form submission
  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Send dates in the backend's expected format, matching
      // CreateStaffModal — raw Date objects serialize to ISO strings
      // the API rejects (CCS-365)
      const response = await createServiceUser({
        ...data,
        dateOfBirth: dateFormat(data.dateOfBirth, "yyyy-MM-dd HH:mm:ss"),
        startDate: dateFormat(data.startDate, "yyyy-MM-dd HH:mm:ss"),
      });
      setNewUserId(response.data);
      reloadUsers();
      setSubmitted(true);
      // setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to create user");
    } finally {
      setIsLoading(false);
    }
  };

  // Form schema configuration
  const userFormSchema = {
    sections: [
      {
        title: "User detail",
        fields: [
          {
            label: "First name",
            key: "firstName",
            type: "text",
            required: true,
          },
          {
            label: "Last name",
            key: "lastName",
            type: "text",
            required: true,
          },
          {
            label: "Preferred name",
            key: "preferredName",
            type: "text",
            required: true,
          },
          {
            label: "Gender",
            key: "gender",
            type: "select",
            options: [
              { label: "Male", value: "male" },
              { label: "Female", value: "female" },
              { label: "Other", value: "other" },
            ],
            required: true,
          },
          {
            label: "Date of birth",
            key: "dateOfBirth",
            type: "date",
            required: true,
          },
          {
            label: "Ethnicity",
            key: "ethnicity",
            type: "select",
            options: [
              { label: "Asian", value: "Asian" },
              {
                label: "Black or African American",
                value: "Black or African American",
              },
              { label: "Hispanic or Latino", value: "Hispanic or Latino" },
              { label: "Middle Eastern", value: "Middle Eastern" },
              { label: "White or Caucasian", value: "White or Caucasian" },
              { label: "Pacific Islander", value: "Pacific Islander" },
              {
                label: "Mixed or Multi-ethnic",
                value: "Mixed or Multi-ethnic",
              },
            ],
            required: true,
          },
          {
            label: "Start date",
            key: "startDate",
            type: "date",
            required: true,
          },
        ],
      },
      {
        title: "Address details",
        fields: [
          {
            label: "Full address",
            key: "address",
            type: "text",
            required: true,
          },
          {
            label: "Postcode",
            key: "postalCode",
            type: "text",
            required: true,
          },
        ],
      },
      {
        title: "Contact details",
        fields: [
          {
            label: "Phone number",
            key: "telephoneNumber",
            type: "text",
            required: true,
          },
          {
            label: "Email address",
            key: "emailAddress",
            type: "email",
            required: false,
          },
        ],
      },
      {
        title: "Next of kin(NOK) details",
        fields: [
          {
            label: "NOK name",
            key: "nextOfKin",
            type: "text",
            required: false,
          },
          {
            label: "NOk contact number",
            key: "nextOfKinContactNumber",
            type: "text",
            required: false,
          },
        ],
      },
      {
        title: "Medication",
        fields: [
          {
            label: "Medication",
            key: "medication",
            type: "multiSelect",
            required: false,
            options: [
              {
                label: "Paracetamol",

                value: "Paracetamol",
              },
              {
                label: "Ibuprofen",

                value: "Ibuprofen",
              },
              {
                label: "Aspirin",

                value: "Aspirin",
              },
              {
                label: "Omeprazole",

                value: "Omeprazole",
              },
              {
                label: "Metformin",

                value: "Metformin",
              },
              {
                label: "Insulin",

                value: "Insulin",
              },
              {
                label: "Atorvastatin",
                value: "Atorvastatin",
              },
              {
                label: "Lisinopril",

                value: "Lisinopril",
              },
              {
                label: "Levothyroxine",
                value: "Levothyroxine",
              },
              {
                label: "Warfarin",

                value: "Warfarin",
              },
              {
                label: "Diazepam",

                value: "Diazepam",
              },
              {
                label: "Sertraline",

                value: "Sertraline",
              },
              {
                label: "Paroxetine",

                value: "Paroxetine",
                usedFor:
                  "SSRI used for depression, anxiety, and other mental health conditions.",
              },
              {
                label: "Simvastatin",

                value: "Simvastatin",
                usedFor:
                  "Statin medication used for reducing cholesterol levels.",
              },
              {
                label: "Albuterol",

                value: "Albuterol",
                usedFor:
                  "Bronchodilator medication used for asthma and other respiratory conditions.",
              },
              {
                label: "Amoxicillin",

                value: "Amoxicillin",
                usedFor:
                  "Antibiotic medication used for various bacterial infections.",
              },
              {
                label: "Azithromycin",
                value: "Azithromycin",
                usedFor:
                  "Antibiotic medication used for respiratory tract and skin infections.",
              },
              {
                label: "Gabapentin",

                value: "Gabapentin",
                usedFor:
                  "Anticonvulsant medication used for epilepsy and neuropathic pain.",
              },
              {
                label: "Pregabalin",

                value: "Pregabalin",
                usedFor:
                  "Anticonvulsant medication used for neuropathic pain and anxiety.",
              },
              {
                label: "Clopidogrel",

                value: "Clopidogrel",
                usedFor: "Antiplatelet medication used to prevent blood clots.",
              },
              {
                label: "Hydrochlorothiazide",
                value: "Hydrochlorothiazide",
              },
              {
                label: "Amlodipine",

                value: "Amlodipine",
              },
              {
                label: "Metoprolol",

                value: "Metoprolol",
              },
              {
                label: "Clozapine",

                value: "Clozapine",
              },
              {
                label: "Risperidone",

                value: "Risperidone",
                usedFor:
                  "Antipsychotic medication used for schizophrenia and bipolar disorder.",
              },
              {
                label: "Fluoxetine",

                value: "Fluoxetine",
                usedFor:
                  "SSRI used for depression, anxiety, and other mental health conditions.",
              },
              {
                label: "Citalopram",

                value: "Citalopram",
                usedFor: "SSRI used for depression and anxiety disorders.",
              },
              {
                label: "Methotrexate",
                value: "Methotrexate",
                usedFor:
                  "Disease-modifying antirheumatic drug (DMARD) used for rheumatoid arthritis and certain cancers.",
              },
              {
                label: "Prednisone",

                value: "Prednisone",
                usedFor:
                  "Corticosteroid medication used for inflammatory conditions.",
              },
              {
                label: "Bupropion",

                value: "Bupropion",
                usedFor:
                  "Antidepressant medication used for depression and smoking cessation.",
              },
              {
                label: "Venlafaxine",

                value: "Venlafaxine",
                usedFor:
                  "Serotonin-norepinephrine reuptake inhibitor (SNRI) used for depression and anxiety.",
              },
              {
                label: "Tramadol",

                value: "Tramadol",
                usedFor: "Opioid analgesic used for moderate to severe pain.",
              },
              {
                label: "Morphine",

                value: "Morphine",
                usedFor: "Strong opioid analgesic used for severe pain.",
              },
              {
                label: "Enoxaparin",

                value: "Enoxaparin",
                usedFor:
                  "Low molecular weight heparin used for preventing blood clots.",
              },
              {
                label: "Ranitidine",

                value: "Ranitidine",
                usedFor:
                  "Histamine H2 receptor antagonist used for acid reflux and ulcers.",
              },
              {
                label: "Pantoprazole",
                value: "Pantoprazole",
                usedFor:
                  "Proton pump inhibitor (PPI) used to reduce stomach acid production.",
              },
              {
                label: "Escitalopram",
                value: "Escitalopram",
                usedFor: "SSRI used for depression and anxiety disorders.",
              },
              {
                label: "Losartan",

                value: "Losartan",
                usedFor:
                  "Angiotensin II receptor blocker (ARB) used for hypertension and heart failure.",
              },
              {
                label: "Carvedilol",

                value: "Carvedilol",
                usedFor:
                  "Beta-blocker used for heart failure and hypertension.",
              },
              {
                label: "Methotrexate",
                value: "Methotrexate",
                usedFor:
                  "Immunosuppressive medication used for autoimmune diseases such as rheumatoid arthritis and psoriasis.",
              },
            ],
          },
        ],
      },
      {
        title: "Medical details",
        fields: [
          {
            label: "DNR(Do Not Resuscitate)",
            key: "dnr",
            type: "checkbox",
            required: false,
          },
          {
            label: "NFC Tag",
            key: "nfcTag",
            type: "text",
            required: false,
          },
          {
            label: "Pharmacy",
            key: "pharmacy",
            type: "text",
            required: false,
          },
          {
            label: "​GP practice",
            key: "gpPractice",
            type: "text",
            required: false,
          },
          {
            label: "​GP address",
            key: "gpContractNumber",
            type: "text",
            required: false,
          },
          {
            label: "​GP contact number",
            key: "gpContactNumber",
            type: "text",
            required: false,
          },
        ],
      },
    ],
  };

  // Render dynamic form fields using the `InputField` component
  const renderField = (sectionKey, field) => {
    const { label, key, type, options, required } = field;

    switch (type) {
      case "text":
      case "email":
      case "date":
        return (
          <InputField
            key={key}
            name={key}
            control={control}
            label={label}
            type={type}
            error={errors[key]?.message}
            required={required}
          />
        );

      case "select":
      case "multiSelect":
        return (
          <SelectField
            {...field}
            key={key}
            control={control}
            label={label}
            name={key}
            options={options}
            required={required}
            error={errors[key]?.message}
            isMulti={type === "multiSelect"}
          />
        );

      case "checkbox":
        return (
          <Controller
            key={key}
            name={key}
            control={control}
            render={({ field }) => (
              <Checkbox
                {...field}
                id={key}
                label={label}
                type={type}
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <Modal
        title="Create service user"
        onCancel={handleCancel}
        isOpen={isModalOpen}
        size="xlg"
      >
        {submitted ? (
          <div className="form__success">
            <h5 className="form__success-title">
              Service user created successfully.
            </h5>
            <IoIosCheckmarkCircleOutline
              size="100px"
              className="form__success-icon"
            />
            <p className="form__success-subtitle">
              Does this user have medication?
            </p>
            <Link to={`/user-medication/${newUseId}`}>
              <button className="btn btn__primary">Add medication</button>
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            {userFormSchema.sections.map((section) => (
              <div key={section.title} className="form__section">
                <h5>{section.title}</h5>
                <div className="form__section-wrap">
                  {section.fields.map((field) =>
                    renderField(section.title, field)
                  )}
                </div>
              </div>
            ))}
            <button
              type="submit"
              className="btn btn__primary btn--block"
              disabled={isLoading}
            >
              {isLoading ? "Loading" : "Submit"}
            </button>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default CreateServiceUser;
